import { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import crm from "../crm/crmApi";
import { fileSrc } from "../crm/website/WebsiteApp";
import {
  MapPin, BadgeCheck, Heart, Share2, Scale, BedDouble, Bath, Maximize2, Phone,
  MessageCircle, CalendarCheck, Download, ChevronLeft, ChevronRight, Check,
  Building2, FileCheck2, Home as HomeIcon,
} from "lucide-react";
import api from "../lib/api";
import { formatINR, configLabel } from "../lib/format";
import { useApp } from "../context/AppContext";
import PropertyCard from "../components/PropertyCard";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const SECTIONS = ["Overview", "Amenities", "Location", "Price", "Developer", "Reviews"];

export default function PropertyDetail() {
  const { slug } = useParams();
  const { openLead, toggleSaved, isSaved, toggleCompare, inCompare } = useApp();
  const [data, setData] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const galleryTouch = useRef(null);
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  const previewId = params.get("preview");

  useEffect(() => {
    setData(null); setError(""); setImgIdx(0);
    (previewId ? crm.wm.preview(previewId) : api.property(slug)).then(setData)
      .catch((e) => setError(e?.response?.status === 404 ? "This property is not available." : previewId ? "Preview needs a CRM login with edit access." : "Could not load this property. Please try again."));
  }, [slug, previewId]);

  useEffect(() => {
    if (!data) return;
    const p = data.property;
    document.title = `${p.seo_title || p.name} | PlotBySquare`;
    let m = document.querySelector('meta[name="description"]');
    if (p.seo_description && m) m.setAttribute("content", p.seo_description);
  }, [data]);

  if (error) return <div className="py-32 text-center" data-testid="property-unavailable"><p className="text-pbs-dark font-bold text-lg">{error}</p><Link to="/properties" className="mt-3 inline-block text-pbs-purple font-bold">Browse available properties</Link></div>;
  if (!data) return <div className="py-32 text-center text-pbs-grey">Loading property…</div>;
  const p = data.property;
  const gallery = (p.gallery?.length ? p.gallery : [p.image]).filter(Boolean).map(fileSrc);
  const isRent = p.listing_type === "rent";

  const share = () => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied to clipboard"); };

  return (
    <div className="bg-white">
      {p.is_preview && <div className="sticky top-0 z-40 bg-pbs-orange text-white text-sm font-bold text-center py-2" data-testid="preview-banner">PREVIEW — draft version, not visible to the public until published</div>}
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        <nav className="text-xs text-pbs-grey flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:text-pbs-purple">Home</Link><span>/</span>
          <Link to={`/city/${p.city.toLowerCase().replace(/\s/g, "-")}`} className="hover:text-pbs-purple">{p.city}</Link><span>/</span>
          <span className="text-pbs-dark font-medium">{p.name}</span>
        </nav>
      </div>

      {/* Gallery */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="grid lg:grid-cols-4 gap-2 rounded-2xl overflow-hidden">
          <div className="lg:col-span-3 relative aspect-[16/10] bg-pbs-bg" data-testid="property-gallery"
            onTouchStart={(e) => { galleryTouch.current = e.touches[0].clientX; }} onTouchEnd={(e) => { if (galleryTouch.current == null) return; const dx = e.changedTouches[0].clientX - galleryTouch.current; galleryTouch.current = null; if (Math.abs(dx) > 48) setImgIdx((i) => (i + (dx < 0 ? 1 : -1) + gallery.length) % gallery.length); }}>
            <img src={gallery[imgIdx]} alt={p.name} className="w-full h-full object-cover" />
            <button onClick={() => setImgIdx((i) => (i - 1 + gallery.length) % gallery.length)} aria-label="Previous photo" data-testid="gallery-prev" className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-[#E5EAE6] text-[#236B45] items-center justify-center shadow-sm"><ChevronLeft size={18} /></button>
            <button onClick={() => setImgIdx((i) => (i + 1) % gallery.length)} aria-label="Next photo" data-testid="gallery-next" className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-[#E5EAE6] text-[#236B45] items-center justify-center shadow-sm"><ChevronRight size={18} /></button>
            <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-md">{imgIdx + 1} / {gallery.length}</span>
          </div>
          <div className="hidden lg:flex flex-col gap-2">
            {gallery.slice(0, 4).map((g, i) => (
              <button key={i} onClick={() => setImgIdx(i)} className={`relative flex-1 rounded-lg overflow-hidden border-2 ${imgIdx === i ? "border-pbs-purple" : "border-transparent"}`}>
                <img src={g} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Header + content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 min-w-0">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                {p.verified && <span className="inline-flex items-center gap-1 bg-pbs-purple text-white text-[11px] font-bold px-2 py-1 rounded-md"><BadgeCheck size={13} /> Verified</span>}
                {p.is_new_launch && <span className="bg-pbs-orange text-white text-[11px] font-bold px-2 py-1 rounded-md">New Launch</span>}
                <span className="text-[11px] font-bold uppercase tracking-wider text-pbs-magenta">{p.property_type}</span>
              </div>
              <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-pbs-dark">{p.name}</h1>
              <p className="mt-1 flex items-center gap-1.5 text-pbs-grey"><MapPin size={16} className="text-pbs-purple" /> {p.locality}, {p.micro_market}, {p.city}</p>
            </div>
            <div className="flex gap-2">
              <IconBtn onClick={() => toggleSaved(p)} active={isSaved(p.slug)} testid="detail-save"><Heart size={18} className={isSaved(p.slug) ? "fill-pbs-coral text-pbs-coral" : ""} /></IconBtn>
              <IconBtn onClick={() => toggleCompare(p)} active={inCompare(p.slug)} testid="detail-compare"><Scale size={18} /></IconBtn>
              <IconBtn onClick={share} testid="detail-share"><Share2 size={18} /></IconBtn>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat icon={BadgeCheck} label="Price" value={isRent ? `${formatINR(p.price)}/mo` : formatINR(p.price)} accent />
            {p.bedrooms > 0 && <Stat icon={BedDouble} label="Config" value={`${p.bedrooms} BHK`} />}
            {p.bathrooms > 0 && <Stat icon={Bath} label="Bathrooms" value={p.bathrooms} />}
            <Stat icon={Maximize2} label="Area" value={`${p.area_sqft.toLocaleString("en-IN")} sq.ft`} />
          </div>

          {/* Quick nav */}
          <div className="mt-6 flex gap-1 overflow-x-auto no-scrollbar border-b border-pbs-line">
            {SECTIONS.map((s) => (
              <a key={s} href={`#sec-${s.toLowerCase()}`} className="px-4 py-2.5 text-sm font-semibold text-pbs-grey hover:text-pbs-purple whitespace-nowrap">{s}</a>
            ))}
          </div>

          {/* Overview */}
          <Block id="sec-overview" title="Overview">
            <p className="text-pbs-grey leading-relaxed">{p.description}</p>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <KV label="Configuration" value={p.configurations?.length ? p.configurations.join(", ") : configLabel(p)} />
              <KV label="Possession" value={p.possession_status} />
              <KV label="Developer" value={p.developer} />
              <KV label="Price / sq.ft" value={p.price_per_sqft ? `₹${p.price_per_sqft.toLocaleString("en-IN")}` : "—"} />
              <KV label="RERA" value={p.rera ? p.rera_id : "—"} />
              <KV label="Status" value={p.ready_to_move ? "Ready to Move" : "Under Construction"} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {p.highlights?.map((h) => <span key={h} className="inline-flex items-center gap-1 text-sm bg-pbs-bg border border-pbs-line px-3 py-1 rounded-full text-pbs-dark"><Check size={13} className="text-pbs-purple" /> {h}</span>)}
            </div>
          </Block>

          {/* Amenities */}
          <Block id="sec-amenities" title="Amenities">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {p.amenities?.map((a) => (
                <div key={a} className="flex items-center gap-2 text-sm text-pbs-dark bg-pbs-bg rounded-lg px-3 py-2.5">
                  <Check size={15} className="text-pbs-purple shrink-0" /> {a}
                </div>
              ))}
            </div>
          </Block>

          {(p.videos?.length > 0 || p.floor_plans?.length > 0 || p.brochure) && (
            <Block id="sec-media" title="Videos, Floor Plans & Brochure">
              {p.videos?.length > 0 && <div className="grid sm:grid-cols-2 gap-3" data-testid="detail-videos">{p.videos.map((v) => <video key={v} src={fileSrc(v)} controls preload="metadata" className="w-full rounded-xl bg-black aspect-video" />)}</div>}
              {p.floor_plans?.length > 0 && <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="detail-floorplans">{p.floor_plans.map((f) => /\.pdf$/i.test(f)
                ? <a key={f} href={fileSrc(f)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 aspect-[4/3] rounded-xl border border-pbs-line bg-pbs-bg text-sm font-semibold text-pbs-purple"><FileCheck2 size={18} /> Floor plan (PDF)</a>
                : <a key={f} href={fileSrc(f)} target="_blank" rel="noreferrer"><img src={fileSrc(f)} alt="Floor plan" className="w-full aspect-[4/3] object-contain rounded-xl border border-pbs-line bg-white" /></a>)}</div>}
              {p.brochure && <a href={fileSrc(p.brochure)} target="_blank" rel="noreferrer" data-testid="detail-brochure-download" className="mt-4 inline-flex items-center gap-2 pbs-gradient text-white font-bold text-sm px-4 py-2.5 rounded-lg"><Download size={16} /> Download Brochure</a>}
            </Block>
          )}

          {/* Location */}
          <Block id="sec-location" title="Location & Connectivity">
            <div className="rounded-xl overflow-hidden border border-pbs-line aspect-[16/7]">
              <iframe title="map" className="w-full h-full" loading="lazy"
                src={`https://maps.google.com/maps?q=${p.lat},${p.lng}&z=13&output=embed`} />
            </div>
            <p className="mt-3 text-sm text-pbs-grey">{p.address || `${p.locality}${p.micro_market ? `, ${p.micro_market}` : ""}, ${p.city}`}.{p.road_width ? ` Well-connected with roads up to ${p.road_width} ft wide${p.gated ? ", within a gated community" : ""}.` : ""}</p>
          </Block>

          {/* Price & Payment */}
          <Block id="sec-price" title="Price & Payment">
            <div className="rounded-xl border border-pbs-line overflow-hidden">
              {[["Base Price", formatINR(p.price)], ["Price per sq.ft", `₹${p.price_per_sqft.toLocaleString("en-IN")}`], ["Area", `${p.area_sqft.toLocaleString("en-IN")} sq.ft`], ["Estimated Registration (approx.)", formatINR(Math.round(p.price * 0.01))], ["Estimated Stamp Duty (approx.)", formatINR(Math.round(p.price * 0.06))]].map(([k, v], i) => (
                <div key={k} className={`flex justify-between px-4 py-3 text-sm ${i % 2 ? "bg-pbs-bg" : "bg-white"}`}>
                  <span className="text-pbs-grey">{k}</span><span className="font-bold text-pbs-dark font-mono">{v}</span>
                </div>
              ))}
            </div>
            <Link to="/calculators" className="mt-3 inline-block text-sm font-semibold text-pbs-purple">Calculate your EMI →</Link>
          </Block>

          {/* Developer */}
          <Block id="sec-developer" title="About the Developer">
            <div className="flex items-center gap-4 p-4 rounded-xl border border-pbs-line">
              <div className="w-14 h-14 rounded-xl pbs-gradient text-white flex items-center justify-center font-extrabold text-lg">{p.developer.split(" ").map((w) => w[0]).join("").slice(0, 2)}</div>
              <div>
                <h4 className="font-bold text-pbs-dark">{p.developer}</h4>
                <p className="text-sm text-pbs-grey">RERA registered developer with a track record of quality delivery.</p>
              </div>
            </div>
          </Block>

          {/* Reviews / FAQ */}
          <Block id="sec-reviews" title="Reviews & FAQs">
            <Accordion type="single" collapsible className="w-full">
              {[["Is this property RERA registered?", p.rera ? `Yes. RERA ID: ${p.rera_id}.` : "RERA details to be updated."],
                ["What is the possession status?", p.ready_to_move ? "This property is ready to move in." : `Expected possession by ${p.possession_status}.`],
                ["Can I schedule a site visit?", "Yes — use the Schedule Visit option and our expert will coordinate a convenient slot."],
                ["Are home loans available?", "Yes, leading banks offer loans for this property subject to eligibility."]].map(([q, a], i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-semibold text-pbs-dark">{q}</AccordionTrigger>
                  <AccordionContent className="text-pbs-grey">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Block>
        </div>

        {/* Sticky enquiry */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 bg-white rounded-2xl border border-pbs-line p-5 pbs-shadow">
            <p className="text-sm text-pbs-grey">{isRent ? "Monthly Rent" : "Price"}</p>
            <p className="text-3xl font-extrabold text-pbs-dark">{isRent ? `${formatINR(p.price)}/mo` : formatINR(p.price)}</p>
            <p className="text-xs text-pbs-grey font-mono mt-0.5">₹{p.price_per_sqft.toLocaleString("en-IN")}/sq.ft</p>
            <div className="mt-4 space-y-2.5">
              <button data-testid="detail-enquire" onClick={() => openLead({ property: p, type: "enquiry", title: `Enquire: ${p.name}` })} className="w-full flex items-center justify-center gap-2 py-3 pbs-gradient text-white font-bold rounded-lg">Send Enquiry</button>
              <div className="grid grid-cols-2 gap-2.5">
                <ActionBtn icon={Phone} label="Call" onClick={() => openLead({ property: p, type: "callback", title: `Call back: ${p.name}` })} testid="detail-call" />
                <ActionBtn icon={MessageCircle} label="WhatsApp" onClick={() => toast.success("Connecting you on WhatsApp…")} testid="detail-whatsapp" />
                <ActionBtn icon={CalendarCheck} label="Visit" onClick={() => openLead({ property: p, type: "visit", title: `Visit: ${p.name}` })} testid="detail-visit" />
                <ActionBtn icon={Download} label="Brochure" onClick={() => openLead({ property: p, type: "brochure", title: `Brochure: ${p.name}` })} testid="detail-brochure" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Similar */}
      {data.similar?.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-pbs-line">
          <h2 className="text-2xl font-extrabold text-pbs-dark mb-6">Similar Properties</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.similar.map((s) => <PropertyCard key={s.slug} property={s} compact />)}
          </div>
        </div>
      )}

      {/* Sticky mobile enquiry */}
      <div className="lg:hidden fixed bottom-16 inset-x-0 z-30 bg-white border-t border-pbs-line p-3 flex gap-2">
        <button onClick={() => openLead({ property: p, type: "callback" })} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-pbs-purple text-pbs-purple font-bold rounded-lg text-sm"><Phone size={15} /> Call</button>
        <button data-testid="mobile-enquire" onClick={() => openLead({ property: p, type: "enquiry", title: `Enquire: ${p.name}` })} className="flex-1 py-2.5 pbs-gradient text-white font-bold rounded-lg text-sm">Enquire Now</button>
      </div>
    </div>
  );
}

const IconBtn = ({ children, onClick, active, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`w-10 h-10 rounded-lg border flex items-center justify-center transition-colors ${active ? "border-pbs-purple text-pbs-purple bg-pbs-purple/5" : "border-pbs-line text-pbs-dark hover:border-pbs-purple"}`}>{children}</button>
);
const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-xl border p-3 ${accent ? "border-pbs-purple/30 bg-pbs-purple/5" : "border-pbs-line bg-white"}`}>
    <Icon size={16} className="text-pbs-purple" />
    <p className="text-[11px] text-pbs-grey mt-1">{label}</p>
    <p className="font-bold text-pbs-dark text-sm leading-tight">{value}</p>
  </div>
);
const Block = ({ id, title, children }) => (
  <section id={id} className="mt-8 scroll-mt-24">
    <h2 className="text-xl font-bold text-pbs-dark mb-4">{title}</h2>
    {children}
  </section>
);
const KV = ({ label, value }) => (
  <div className="bg-pbs-bg rounded-lg px-3 py-2.5"><p className="text-[11px] text-pbs-grey">{label}</p><p className="font-semibold text-pbs-dark text-sm">{value}</p></div>
);
const ActionBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="flex items-center justify-center gap-1.5 py-2.5 border border-pbs-line rounded-lg text-sm font-semibold text-pbs-dark hover:border-pbs-purple hover:text-pbs-purple transition-colors"><Icon size={15} /> {label}</button>
);
